import type { ItemInstance, WeaponItemDef } from "../items/types.js";

/**
 * What firing a gun costs the gun — the counterpart to `combat/armor.ts`, in
 * the same `ItemInstance.durability` field. ABSENT DURABILITY READS AS FULL,
 * which is why no migration was needed: every gun minted before this file is
 * in perfect condition. Wear is server-authoritative (`resolveFires`); the
 * accuracy penalty below is client-side feel, like `recoil`.
 */

/**
 * Durability one trigger pull costs, whatever the gun: 500 shots from fresh to
 * dead — a HANDFUL OF RAIDS, enough that guns leave the economy, not so few
 * that anybody watches a rifle degrade inside one firefight. Flat across the
 * roster: what differs between guns is how many rounds they send.
 */
export const WEAPON_WEAR_PER_SHOT = 0.2;

/**
 * Below this fraction of the ceiling, shots start walking off the sights. Seven
 * tenths of a gun's life are penalty-free — a cliff the player can see coming,
 * not a tax from the first shot.
 */
export const WEAR_ACCURACY_FRAC = 0.3;

/**
 * Extra cone half-angle, degrees, on a gun with nothing left. Sized against the
 * roster's 2.5°–4.5° hip cones: felt at range, never a blunderbuss. Applied to
 * AIMED fire too.
 */
export const WEAR_MAX_EXTRA_SPREAD_DEG = 0.8;

/**
 * The ceiling THIS gun can be filled to — its own shaved one if repaired, the
 * def's otherwise. `armorMaxDurability`'s twin (`economy/repair.ts`).
 */
export function weaponMaxDurability(def: WeaponItemDef, item: ItemInstance): number {
  return item.maxDurability ?? def.maxDurability;
}

/**
 * How much gun is left, in points. ABSENT IS FULL — of the instance's ceiling,
 * not the def's.
 */
export function weaponDurability(def: WeaponItemDef, item: ItemInstance): number {
  return item.durability ?? weaponMaxDurability(def, item);
}

/** One decimal, and no float dust. What both the wire and the tooltip see.
 *  The same rounding `economy/repair.ts` applies to a repaired ceiling. */
function round1(x: number): number {
  return Math.round(x * 10) / 10;
}

/**
 * The gun one trigger pull later — a replacement, folded into the same
 * replacement the magazine decrement produces. ROUNDED TO ONE DECIMAL every
 * shot (0.2 is not representable; five hundred raw subtractions would leave
 * 1.4e-14 instead of 0) and CLAMPED AT ZERO. `wearMult` is the gunsmith
 * skill's multiplier on the cost of the pull, never on the ceiling; the
 * default 1 is bit-identical to no skill.
 */
export function wearWeapon(
  def: WeaponItemDef,
  item: ItemInstance,
  wearMult = 1,
): ItemInstance {
  // Garbage is no multiplier at all; a negative one is a gun that never wears.
  const mult = Number.isFinite(wearMult) ? (wearMult > 0 ? wearMult : 0) : 1;
  const next = weaponDurability(def, item) - WEAPON_WEAR_PER_SHOT * mult;
  return { ...item, durability: next <= 0 ? 0 : round1(next) };
}

/**
 * A gun that will not fire at all — the only part of the mechanic the server
 * enforces (`acceptFire` refuses with `"broken"`). No misfire, no jam: the gun
 * works, then it is loose, then it stops.
 */
export function isWeaponBroken(def: WeaponItemDef, item: ItemInstance): boolean {
  return weaponDurability(def, item) <= 0;
}

/**
 * Extra cone half-angle in DEGREES from condition: zero at or above
 * `WEAR_ACCURACY_FRAC` of the instance's ceiling, linear to
 * `WEAR_MAX_EXTRA_SPREAD_DEG` at nothing. CLIENT-SIDE FEEL ONLY — never enters
 * `WeaponStats`; read by the client's fire path alone. Scav fire ignores it
 * (their error is `ScavBrain`'s own cone).
 */
export function wearExtraSpreadDeg(def: WeaponItemDef, item: ItemInstance): number {
  const max = weaponMaxDurability(def, item);
  if (!(max > 0)) return WEAR_MAX_EXTRA_SPREAD_DEG;

  const knee = max * WEAR_ACCURACY_FRAC;
  const cur = weaponDurability(def, item);
  if (cur >= knee) return 0;
  if (cur <= 0) return WEAR_MAX_EXTRA_SPREAD_DEG;
  return WEAR_MAX_EXTRA_SPREAD_DEG * (1 - cur / knee);
}
